import { PaymentLinkStatus } from '@utriper/core';
import React, { useContext, useState } from 'react';
import { navigateTo } from '../navigate';
import { CheckoutService } from 'core/api/services/checkout';
import { useStripe } from './stripe';
import { useTranslation } from '../../core/contexts/localization';

const CheckoutContext = React.createContext(null);

export const CheckoutProvider = ({ children }) => {
    const [accessCode, setAccessCode] = useState(null);
    const [paymentLink, setPaymentLink] = useState(null);
    const [paymentLog, setPaymentLog] = useState(null);
    const [paymentId, setPaymentId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [bookingItems, setBookingItems] = useState([]);
    const [bookingCode, setBookingCode] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(PaymentLinkStatus.IDS.created);
    const [redirectUrl, setRedirectUrl] = useState(null);
    const [autoRedirect, setAutoRedirect] = useState(false);
    const [prices, setPrices] = useState({
        totalPriceCOP: 0,
        totalPriceNowCOP: 0,
        totalPriceUSD: 0,
        totalPriceNowUSD: 0,
    });
    const [termsToAccept, setTermsToAccept] = useState([]);
    const [loadingTerms, setLoadingTerms] = useState(false);
    const [experienceTerms, setExperienceTerms] = useState(null);

    const { setStripeClientSecret, setStripeOpen } = useStripe();
    const { i18n } = useTranslation();

    const validateCheckout = async (accessCode) => {
        try {
            setLoading(true);

            const { paymentLink, paymentLog, paymentStatus, redirectUrl, autoRedirect } = await CheckoutService.validateCheckout(accessCode);

            setPaymentLink(paymentLink);
            setPaymentLog(paymentLog);
            setPaymentStatus(paymentStatus);
            // Kept in their own state rather than folded into paymentLink: paymentLink mirrors
            // a database row, while these are resolved server-side per request.
            setRedirectUrl(redirectUrl ?? null);
            setAutoRedirect(Boolean(autoRedirect));

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const getBookingItems = async (bookingCode) => {
        try {
            setLoading(true);
            const { items, termsToAccept, prices } = await CheckoutService.getBookingItems(bookingCode);

            if (!items) {
                throw new Error('No hay Experiencias asociadas a esta reserva');
            }

            setBookingItems(items);
            setPrices({ ...prices });
            setBookingCode(items[0]?.code);
            setTermsToAccept(termsToAccept);

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const onStripeCreateOrder = async (userData) => {
        try {
            const data = await CheckoutService.createStripeSession(bookingCode, userData, accessCode, termsToAccept);

            setStripeClientSecret(data?.id);
            setPaymentId(data?.paymentId);
            setStripeOpen(true);
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const onDismissConfirmPayment = async (paymentId) => {
        try {
            const data = await CheckoutService.cancelStripePayment({ paymentId });

            if (!data) {
                throw new Error('Error canceling payments');
            }

            return data;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const cancelStatusPaymentLink = async () => {
        try {
            setLoading(true);
            const bookingData = await CheckoutService.cancelStatusPaymentLink({ paymentLink: accessCode, cancelBooking: true });
            if (!bookingData) {
                throw new Error();
            }
            // Deliberately paymentLink.callbackURL, NOT redirectUrl. A contract's redirect is
            // its thank-you page, and a cancelled booking must never land there — that would
            // record an advertising conversion that did not happen, which is the opposite of
            // what roadmap#684 exists to measure.
            navigateTo(paymentLink.callbackURL || 'https://experiences.utriper.com/');
            return bookingData;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const onEpaycoCreateOrder = async (userData) => {
        try {
            const sessionId = await CheckoutService.createEpaycoSession(bookingCode, userData, accessCode, i18n.language);

            if (!sessionId) {
                throw new Error('No session id provided');
            }

            const handler = window.ePayco.checkout.configure({
                sessionId,
            });

            handler.openNew();
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const onBookCreateOrder = async (userData) => {
        try {
            const data = await CheckoutService.updatePaymentLink(userData, accessCode);

            if (!data) {
                throw new Error('There was an error confirming the reservation');
            }

            window.location.reload();
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const getExperienceTermsId = async (expId, termsId) => {
        try {
            setLoadingTerms(true);
            if (experienceTerms?.id === termsId) {
                return { experienceTerms };
            }
            const result = await CheckoutService.getExperiencesTermsId(expId, termsId);

            if (result?.experienceTerms) {
                setExperienceTerms(result?.experienceTerms);
            } else {
                setExperienceTerms(null);
            }

            return result;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingTerms(false);
        }
    };

    return (
        <CheckoutContext.Provider
            value={{
                redirectUrl,
                autoRedirect,
                validateCheckout,
                getBookingItems,
                onStripeCreateOrder,
                onEpaycoCreateOrder,
                onBookCreateOrder,
                onDismissConfirmPayment,
                setAccessCode,
                cancelStatusPaymentLink,
                loading,
                paymentLink,
                paymentLog,
                bookingItems,
                prices,
                paymentStatus,
                paymentId,
                termsToAccept,
                loadingTerms,
                experienceTerms,
                getExperienceTermsId,
            }}>
            {children}
        </CheckoutContext.Provider>
    );
};

CheckoutProvider.displayName = 'CheckoutProvider';

export const useCheckout = () => useContext(CheckoutContext);
